import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Modal } from 'react-bootstrap'
import styled from '@emotion/styled'

import { CurrencyInput } from '@components/common/form'
import { AutocompleteInput } from '@containers/common/form'
import {
  CHEF_PAY_CATEGORIES,
  CHEF_PAY_CATEGORY_OTHER_MANUAL,
} from '../../../../constants'

const InitialState = {
  payouts: [],
  showAddChef: false,
  isSaving: false,
  chefsToRemove: [],
  chefsOnOrder: [],
}

class EditChefPayoutsModal extends Component {
  state = InitialState

  componentDidMount() {
    // Initialize payouts from payableChefPayoutSummaries
    const { payableChefPayoutSummaries = [] } = this.props
    this.setState({
      payouts: payableChefPayoutSummaries.map((summary) => ({
        id: summary.id,
        name: summary.name,
        preAdjustmentAmount: parseFloat(summary.preAdjustmentAmount) || 0,
        postAdjustmentAmount: parseFloat(summary.postAdjustmentAmount) || 0,
        category: summary.details?.category || '',
        notes: summary.details?.notes || '',
        adjustedByEmail: summary.details?.adjustedByEmail || null,
        adjustedAt: summary.details?.adjustedAt || null,
        isNew: false,
        isMarkedForRemoval: false,
      })),
      chefsToRemove: [],
      chefsOnOrder: this.props.chefs || [],
    })
  }

  isPayoutAdjusted = (payout) => {
    return payout.preAdjustmentAmount !== payout.postAdjustmentAmount
  }

  isAdditionalChefPayout = (payout) => {
    return (
      this.state.chefsOnOrder.find((chef) => chef.id === payout.id) ===
      undefined
    )
  }

  isNotesRequired = (category) => {
    return category === CHEF_PAY_CATEGORY_OTHER_MANUAL
  }

  isNotesValid = (category, notes) => {
    if (!this.isNotesRequired(category)) {
      return true
    }
    const trimmedNotes = (notes || '').trim()

    return trimmedNotes.length >= 5
  }

  isCategoryValid = (category) => {
    return !!category && category.trim().length > 0
  }

  getPayoutValidationErrors = (payout) => {
    const errors = []

    if (!payout.isMarkedForRemoval) {
      if (this.isPayoutAdjusted(payout) || payout.isNew) {
        if (!this.isCategoryValid(payout.category)) {
          errors.push('Category is required')
        }
        if (!this.isNotesValid(payout.category, payout.notes)) {
          errors.push(
            'Notes are required (min 5 chars) for "Other – Manual adjustment"',
          )
        }
      }
    }

    return errors
  }

  hasAnyValidationErrors = () => {
    return this.state.payouts.some(
      (p) => this.getPayoutValidationErrors(p).length > 0,
    )
  }

  adjustPayout = (chefId, newAmount) => {
    this.setState((state) => ({
      payouts: state.payouts.map((p) =>
        p.id === chefId ? { ...p, postAdjustmentAmount: newAmount } : p,
      ),
    }))
  }

  updatePayoutCategory = (chefId, category) => {
    this.setState((state) => ({
      payouts: state.payouts.map((p) =>
        p.id === chefId
          ? {
              ...p,
              category,
            }
          : p,
      ),
    }))
  }

  updatePayoutNotes = (chefId, notes) => {
    this.setState((state) => ({
      payouts: state.payouts.map((p) =>
        p.id === chefId ? { ...p, notes } : p,
      ),
    }))
  }

  onAdjust50 = (payout) => () => {
    this.adjustPayout(payout.id, payout.postAdjustmentAmount / 2)
  }

  onChangePayout = (payout) => (val) => {
    this.adjustPayout(payout.id, val)
  }

  onChangeCategory = (payout) => (e) => {
    this.updatePayoutCategory(payout.id, e.target.value)
  }

  onChangeNotes = (payout) => (e) => {
    this.updatePayoutNotes(payout.id, e.target.value)
  }

  toggleAddChef = () => {
    this.setState((state) => ({ showAddChef: !state.showAddChef }))
  }

  onSelectChef = (chef) => {
    // Check if chef already exists in payouts
    const existingPayout = this.state.payouts.find((p) => p.id === chef.id)
    if (existingPayout) {
      this.props.displayWarningMessage(
        'This chef already has a payout on this order',
      )

      return
    }

    // Add new chef payout with 0 initial value
    this.setState((state) => ({
      payouts: [
        ...state.payouts,
        {
          id: chef.id,
          name: chef.name,
          preAdjustmentAmount: 0,
          postAdjustmentAmount: 0,
          category: '',
          notes: '',
          isNew: true,
          isMarkedForRemoval: false,
        },
      ],
      showAddChef: false,
    }))
  }

  onRemoveChef = (chefId) => () => {
    this.setState((state) => {
      const payout = state.payouts.find((p) => p.id === chefId)
      if (payout.isNew) {
        // For new chefs, just remove from the list
        return {
          payouts: state.payouts.filter((p) => p.id !== chefId),
        }
      } else {
        // For existing chefs, mark for removal
        return {
          payouts: state.payouts.map((p) =>
            p.id === chefId ? { ...p, isMarkedForRemoval: true } : p,
          ),
          chefsToRemove: [...state.chefsToRemove, chefId],
        }
      }
    })
  }

  onUndoRemove = (chefId) => () => {
    this.setState((state) => ({
      payouts: state.payouts.map((p) =>
        p.id === chefId ? { ...p, isMarkedForRemoval: false } : p,
      ),
      chefsToRemove: state.chefsToRemove.filter((id) => id !== chefId),
    }))
  }

  onHide = () => this.props.closeChefPayouts()

  onSave = async () => {
    // Validate all payouts before saving
    if (this.hasAnyValidationErrors()) {
      this.props.displayWarningMessage(
        'Please fix validation errors before saving. Category is required for adjusted payouts, and Notes are required when category is "Other – Manual adjustment".',
      )

      return
    }

    // Prepare adjustments - only include adjusted or new payouts that aren't marked for removal
    const adjustments = this.state.payouts
      .filter(
        (p) => !p.isMarkedForRemoval && (this.isPayoutAdjusted(p) || p.isNew),
      )
      .map((p) => ({
        chefId: p.id,
        grossPay: p.postAdjustmentAmount,
        category: p.category,
        notes: p.notes || undefined,
      }))

    // Get chef IDs to remove
    const removeChefIds = this.state.chefsToRemove

    this.setState({ isSaving: true })
    try {
      const result = await this.props.adjustChefPayoutsWithCategory({
        orderId: this.props.orderId,
        adjustments,
        removeChefIds,
      })

      if (result && result.success) {
        this.onHide()
      }
    } finally {
      this.setState({ isSaving: false })
    }
  }

  renderPayouts = () => {
    return this.state.payouts.map((payout) => {
      const { isNew: isNewChef, isMarkedForRemoval } = payout
      const isAdjusted = this.isPayoutAdjusted(payout)
      const isAdditionalChefPayout = this.isAdditionalChefPayout(payout)
      const showCategoryNotes = isAdjusted || isNewChef
      const validationErrors = this.getPayoutValidationErrors(payout)
      const showNotesField = this.isNotesRequired(payout.category)

      if (isMarkedForRemoval) {
        return (
          <PayoutRow key={payout.id} isMarkedForRemoval>
            <ChefNameContainer>
              <ChefName>
                Chef {payout.name}
                <RemovalBadge>WILL BE REMOVED</RemovalBadge>
              </ChefName>
            </ChefNameContainer>
            <RemovalNotice>
              This chef payout will be removed when you save.
              <UndoButton onClick={this.onUndoRemove(payout.id)}>
                Undo
              </UndoButton>
            </RemovalNotice>
          </PayoutRow>
        )
      }

      return (
        <PayoutRow key={payout.id}>
          <ChefNameContainer>
            <ChefName>
              Chef {payout.name}
              {isNewChef && <NewBadge>NEW</NewBadge>}
              {isAdjusted && !isNewChef && !isAdditionalChefPayout && (
                <AdjustedBadge>ADJUSTED</AdjustedBadge>
              )}
              {isAdditionalChefPayout && (
                <AdditionalChefBadge>ADDITIONAL</AdditionalChefBadge>
              )}
            </ChefName>
            <PreAdjustmentAmount>
              Original: ${(payout.preAdjustmentAmount || 0.0).toFixed(2)}
            </PreAdjustmentAmount>
          </ChefNameContainer>

          <FieldRow>
            <FieldLabel>Gross Pay</FieldLabel>
            <CurrencyInput
              value={payout.postAdjustmentAmount || 0}
              onChange={this.onChangePayout(payout)}
            />
          </FieldRow>

          {showCategoryNotes && (
            <>
              <FieldRow>
                <FieldLabel>
                  Category <RequiredStar>*</RequiredStar>
                </FieldLabel>
                <CategorySelect
                  value={payout.category || ''}
                  onChange={this.onChangeCategory(payout)}
                  hasError={!this.isCategoryValid(payout.category)}
                >
                  <option value="">-- Select Category --</option>
                  {CHEF_PAY_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </CategorySelect>
              </FieldRow>

              <FieldRow>
                <FieldLabel>
                  Notes {showNotesField && <RequiredStar>*</RequiredStar>}
                </FieldLabel>
                <NotesTextarea
                  placeholder={
                    showNotesField
                      ? 'Enter notes (min 5 characters required)...'
                      : 'Optional notes...'
                  }
                  value={payout.notes || ''}
                  onChange={this.onChangeNotes(payout)}
                  hasError={!this.isNotesValid(payout.category, payout.notes)}
                />
                {showNotesField && (
                  <CharCount isError={(payout.notes || '').trim().length < 5}>
                    {(payout.notes || '').trim().length} / 5 min
                  </CharCount>
                )}
              </FieldRow>

              {validationErrors.length > 0 && (
                <ValidationErrors>
                  {validationErrors.map((err, idx) => (
                    <ValidationError key={idx}>⚠️ {err}</ValidationError>
                  ))}
                </ValidationErrors>
              )}
            </>
          )}

          {(payout.adjustedByEmail || payout.adjustedAt) && (
            <AuditInfo>
              <AuditLabel>Last Adjusted:</AuditLabel>
              <AuditValue>
                {payout.adjustedByEmail || 'Unknown'}{' '}
                {payout.adjustedAt && (
                  <AuditTimestamp>
                    on {new Date(payout.adjustedAt).toLocaleString()}
                  </AuditTimestamp>
                )}
              </AuditValue>
            </AuditInfo>
          )}

          <ButtonGroup>
            <button
              className="btn btn-link"
              id="adjust-50"
              onClick={this.onAdjust50(payout)}
            >
              Pay 50%
            </button>
            {isAdjusted && (
              <button
                className="btn btn-link"
                id="adjust-default"
                onClick={this.onRemoveChef(payout.id)}
              >
                {isAdditionalChefPayout ? 'Remove' : 'Reset to Original'}
              </button>
            )}
          </ButtonGroup>
        </PayoutRow>
      )
    })
  }

  render() {
    const { loadChefs } = this.props
    const { showAddChef, isSaving } = this.state

    return (
      <Modal
        show={this.props.show}
        onHide={this.onHide}
        dialogClassName="chefPayoutsModal chef-payouts-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-lg">
            Adjust Chef Payouts
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="padding-20">
            <ModalDescription>
              Adjust chef gross payout amount. Fees and taxes are still
              generated for the newly adjusted gross payout amounts but Volume
              Rebates are not - so please include any volume rebates in the
              gross amount if necessary.
            </ModalDescription>
            <div className="form-field-container">{this.renderPayouts()}</div>

            <AddChefSection>
              {showAddChef ? (
                <AddChefForm>
                  <AddChefTitle>Add Chef Payout</AddChefTitle>
                  <HelperText>
                    Search for a chef to add their payout to this order.
                  </HelperText>
                  <AutocompleteInput
                    label="Search Chef"
                    loaderFunction={(search) => loadChefs({ ...search })}
                    value=""
                    onSelect={this.onSelectChef}
                  />
                  <button className="btn btn-link" onClick={this.toggleAddChef}>
                    Cancel
                  </button>
                </AddChefForm>
              ) : (
                <AddChefButton
                  className="button-neutral"
                  onClick={this.toggleAddChef}
                  disabled={isSaving}
                >
                  + Add Chef Payout
                </AddChefButton>
              )}
            </AddChefSection>

            <div className="mt-25">
              <SaveButton
                className="button-primary"
                id="save-chef-payouts"
                onClick={this.onSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Spinner /> Saving...
                  </>
                ) : (
                  'Save'
                )}
              </SaveButton>
              <button
                className="button-neutral"
                id="cancel"
                onClick={this.onHide}
                disabled={isSaving}
              >
                {' '}
                Cancel{' '}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    )
  }
}

EditChefPayoutsModal.propTypes = {
  payableChefPayoutSummaries: PropTypes.array,
  chefs: PropTypes.array,

  show: PropTypes.bool,

  adjustChefPayoutsWithCategory: PropTypes.func,
  changeOrderStatus: PropTypes.func,
  closeChefPayouts: PropTypes.func,
  displayWarningMessage: PropTypes.func,
  loadChefs: PropTypes.func,

  orderId: PropTypes.string,
  orderStatus: PropTypes.string,
}

// Styled Components
const PayoutRow = styled.div`
  margin-bottom: 12px;
  padding: 12px;
  background: ${(props) => (props.isMarkedForRemoval ? '#ffebee' : '#f8f9fa')};
  border-radius: 6px;
  border: 1px solid
    ${(props) => (props.isMarkedForRemoval ? '#f44336' : '#e0e0e0')};
  opacity: ${(props) => (props.isMarkedForRemoval ? 0.8 : 1)};
`

const ChefNameContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const ChefName = styled.span`
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`

const PreAdjustmentAmount = styled.span`
  font-size: 12px;
  color: #666;
  font-style: italic;
`

const NewBadge = styled.span`
  background: #2196f3;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.5px;
`

const AdjustedBadge = styled.span`
  background: #ff9800;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.5px;
`

const AdditionalChefBadge = styled.span`
  background: #673ab7;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.5px;
`

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
`

const AddChefSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px dashed #ccc;
`

const AddChefButton = styled.button`
  width: 100%;
  padding: 12px;
  border: 2px dashed #ccc;
  background: transparent;
  color: #666;
  font-weight: 600;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    border-color: #999;
    color: #333;
    background: #f5f5f5;
  }
`

const AddChefForm = styled.div`
  background: #f5f5f5;
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 15px;
`

const AddChefTitle = styled.h4`
  font-size: 14px;
  font-weight: bold;
  margin: 0 0 8px 0;
`

const HelperText = styled.p`
  font-size: 12px;
  color: #666;
  font-style: italic;
  margin: 0 0 12px 0;
`

const ModalDescription = styled.p`
  font-size: 13px;
  font-weight: bold;
  color: #666;
  line-height: 1.4;
  margin: 10px 0 10px 0;
  padding: 12px;
`

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const RemovalBadge = styled.span`
  background: #f44336;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.5px;
`

const RemovalNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
  background: #ffcdd2;
  border-radius: 4px;
  font-size: 13px;
  color: #c62828;
`

const UndoButton = styled.button`
  background: #fff;
  color: #f44336;
  border: 1px solid #f44336;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f44336;
    color: white;
  }
`

const FieldRow = styled.div`
  margin-bottom: 12px;
`

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const RequiredStar = styled.span`
  color: #f44336;
`

const CategorySelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  border: 2px solid ${(props) => (props.hasError ? '#f44336' : '#e0e0e0')};
  border-radius: 6px;
  background: white;
  color: #333;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? '#f44336' : '#2196f3')};
  }
`

const NotesTextarea = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 10px 12px;
  font-size: 14px;
  border: 2px solid ${(props) => (props.hasError ? '#f44336' : '#e0e0e0')};
  border-radius: 6px;
  background: white;
  color: #333;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? '#f44336' : '#2196f3')};
  }

  &::placeholder {
    color: #999;
  }
`

const CharCount = styled.span`
  display: block;
  text-align: right;
  font-size: 11px;
  margin-top: 4px;
  color: ${(props) => (props.isError ? '#f44336' : '#999')};
  font-weight: ${(props) => (props.isError ? '600' : 'normal')};
`

const ValidationErrors = styled.div`
  margin-top: 8px;
  padding: 8px;
  background: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;
`

const ValidationError = styled.p`
  margin: 0;
  padding: 2px 0;
  font-size: 12px;
  color: #c62828;
  font-weight: 500;
`

const AuditInfo = styled.div`
  margin-top: 10px;
  padding: 8px 12px;
  background: #e3f2fd;
  border-radius: 4px;
  border: 1px solid #90caf9;
`

const AuditLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #1565c0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-right: 6px;
`

const AuditValue = styled.span`
  font-size: 12px;
  color: #1976d2;
  font-weight: 500;
`

const AuditTimestamp = styled.span`
  font-size: 11px;
  color: #64b5f6;
  font-style: italic;
`

export default EditChefPayoutsModal
